import {decodeAuthTokenClaims} from "./userAccountSession";
import {terms} from "shared/brand/terms";

const DRIVE_TOKEN_KEY = "company-files-google-drive-token";
const SESSION_CHANGED = "hk-auth-session-changed";
let revision = 0;

export type AuthSession = {token: string; revision: number};
export type GoogleDriveGrant = {accessToken: string; expiresAt: number; session: AuthSession};

export function clearStoredGoogleDriveToken(): void {
    for (const name of ["localStorage", "sessionStorage"] as const) {
        try { window[name].removeItem(DRIVE_TOKEN_KEY); } catch { /* Storage may be disabled. */ }
    }
}

function sessionChanged(): void {
    revision += 1;
    clearStoredGoogleDriveToken();
    window.dispatchEvent(new Event(SESSION_CHANGED));
}

/** All same-tab login/logout paths must invalidate linked credentials synchronously. */
export function setStoredAuthToken(token: string | null): void {
    if (token) window.localStorage.setItem("authToken", token);
    else window.localStorage.removeItem("authToken");
    sessionChanged();
}

if (typeof window !== "undefined") {
    window.addEventListener("storage", event => {
        if (event.storageArea === window.localStorage && (event.key === "authToken" || event.key === null)) {
            sessionChanged();
        }
    });
}

export function subscribeAuthSessionChange(listener: () => void): () => void {
    window.addEventListener(SESSION_CHANGED, listener);
    return () => window.removeEventListener(SESSION_CHANGED, listener);
}

export function captureAuthSession(): AuthSession {
    const token = window.localStorage.getItem("authToken");
    const claims = decodeAuthTokenClaims(token);
    if (!token || !claims?.exp || claims.exp * 1000 <= Date.now()) {
        throw new Error(`החיבור ל־${terms.brand} הסתיים. יש להתחבר מחדש.`);
    }
    return {token, revision};
}

export function isCurrentAuthSession(session: AuthSession): boolean {
    try {
        const current = captureAuthSession();
        return current.token === session.token && current.revision === session.revision;
    } catch { return false; }
}

async function sessionHash(session: AuthSession): Promise<string> {
    const hash = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(session.token));
    return Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, "0")).join("");
}

export async function readStoredGoogleDriveToken(session: AuthSession): Promise<GoogleDriveGrant | null> {
    if (!isCurrentAuthSession(session)) return null;
    // Old tab-scoped, unbound grants must never be promoted into a new HK session.
    window.sessionStorage.removeItem(DRIVE_TOKEN_KEY);
    const raw = window.localStorage.getItem(DRIVE_TOKEN_KEY);
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        const owner = await sessionHash(session);
        if (!isCurrentAuthSession(session)) return null;
        if (typeof parsed?.accessToken !== "string" || !parsed.accessToken ||
            !Number.isFinite(parsed.expiresAt) || parsed.expiresAt <= Date.now() || parsed.sessionHash !== owner) {
            clearStoredGoogleDriveToken();
            return null;
        }
        return {accessToken: parsed.accessToken, expiresAt: parsed.expiresAt, session};
    } catch {
        if (isCurrentAuthSession(session)) clearStoredGoogleDriveToken();
        return null;
    }
}

export async function persistGoogleDriveToken(session: AuthSession, accessToken: string, expiresInSeconds = 3600, isActive = () => true): Promise<GoogleDriveGrant> {
    const owner = await sessionHash(session);
    if (!isActive() || !isCurrentAuthSession(session)) throw new Error(`חשבון ${terms.brand} השתנה. יש להתחבר ל־Google Drive מחדש.`);
    const duration = Number(expiresInSeconds);
    if (!accessToken || !Number.isFinite(duration) || duration <= 0) throw new Error("התקבלה הרשאת Google Drive לא תקינה.");
    const expiresAt = Date.now() + Math.max(duration - 60, 0) * 1000;
    window.localStorage.setItem(DRIVE_TOKEN_KEY, JSON.stringify({accessToken, expiresAt, sessionHash: owner}));
    window.sessionStorage.removeItem(DRIVE_TOKEN_KEY);
    return {accessToken, expiresAt, session};
}
